import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  claimantLink,
  fileCount,
  filesOrFormField,
  maskedIdentifier,
  sortValues,
  shortDateOrBlank,
  statusField,
} from './CombinedHistoryFields';
import { ViewFilesButton, hasFiles } from './SubmissionFilesModal';
import { submissionPropType } from '../utilities/combinedHistory';
import {
  COMBINED_HISTORY_COLUMNS,
  COMBINED_HISTORY_DIRECTIONS,
} from '../utilities/constants';

/** @typedef {import('../utilities/combinedHistory').CombinedHistorySubmission} Submission */

/**
 * @param {string|number|JSX.Element|null|undefined} value A cell's content.
 * @returns {string|number|JSX.Element} The content, or for a blank value,
 *   "None" that only a screen reader announces, so a blank cell isn't
 *   mistaken for a skipped one.
 */
const orNone = value =>
  value === '' || value == null ? (
    <span className="vads-u-visibility--screen-reader">None</span>
  ) : (
    value
  );

// The received date under a status badge.
const STATUS_DATE =
  'submissions__status-date vads-u-display--block vads-u-margin-top--0p25 vads-u-font-size--sm';

/**
 * @typedef {Object} SortRestore
 * @property {number} columnIndex The sorted column.
 * @property {string} direction One of `COMBINED_HISTORY_DIRECTIONS`.
 * @property {number} token Identifies the sort a reader asked for, reported
 *   back through `onRestored`.
 */

/**
 * va-table's `sortdir` for each of the history's directions.
 *
 * @type {Readonly<Record<string, 'ascending'|'descending'>>}
 */
const VA_TABLE_DIRECTIONS = Object.freeze({
  [COMBINED_HISTORY_DIRECTIONS.ASC]: 'ascending',
  [COMBINED_HISTORY_DIRECTIONS.DESC]: 'descending',
});

/**
 * How long the wrapper holds its height after a reader's sort before giving
 * up on the restore that normally releases it.
 *
 * @type {number}
 */
const HEIGHT_HOLD_FALLBACK_MS = 2000;

/**
 * @param {CustomEvent} event A `sortTable` event.
 * @returns {boolean} Whether it is a reader's sort, with the column and
 *   direction va-table-inner reports, rather than the history's own restore
 *   or a bare event.
 */
const isReaderSort = event =>
  event.detail?.index !== undefined &&
  event.detail?.sortdir !== undefined &&
  !event.detail.restored;

/**
 * Sortable table of submissions sent through the portal. `CombinedHistory`
 * renders it once the route loader has rows and shows the error and empty
 * states itself. The columns follow the design: Submission, which is the
 * date over the type, Claimant, Status, Confirmation #, and Files / Form,
 * each sorted by its value from `sortValues`. The title is the table's
 * caption, which is its accessible name and the name va-table's sort
 * announcement uses. va-table-inner gives the caption no part, so it shows
 * as the design system styles it.
 *
 * The history pages in the browser, so the table gets one page of rows and
 * the whole list is sorted outside it. va-table sorts only the rows it holds,
 * so a reader's sort is taken from it before it sorts: the wrapper's capture
 * listener stops `sortTable` and reports the column and direction through
 * `onSortRequest`. The history then sorts the whole list, returns to its
 * first page, and remounts the table with a new `tableKey`. Remounting
 * matters: va-table moves each row's cells out of its `va-table-row`, so
 * React must never update rows va-table has already taken apart.
 * An empty list renders nothing, because va-table throws while loading with
 * no data row.
 *
 * va-table keeps its sort state to itself and forgets it on every rebuild.
 * After a reader's sort the history passes `restoreSort`, and the table
 * dispatches one `sortTable` of its own, marked `restored`, so va-table sorts
 * the already-ordered page (a no-op) and rebuilds with its arrow, `aria-sort`,
 * focus on the header, and announcement — what a reader who clicked a header
 * expects. The index is sent as a string: va-table seeds its state only when
 * the index is truthy, and the first column is `0`. The parent passes a
 * restore only on the first page and only for the sort in force, and clears
 * it once `onRestored` reports it, so a table that mounts again for Refresh
 * or a view switch does not sort or move focus on its own. A page change,
 * a search, and a filter change pass no restore, since the restore would
 * move focus while the reader is elsewhere, so after one the arrow is gone;
 * that is a known gap
 * with the design system until va-table can be told its sort.
 *
 * The wrapper holds its height while va-table-inner is replaced: va-table
 * replaces it on every sort, and a layout in between shortens the page and
 * jumps the scroll. The hold and the restore depend on va-table replacing
 * `va-table-inner` on sort, exposing `componentOnReady`, and seeding its
 * state from the `sortTable` detail; a component-library release that
 * changes any of those makes them inert, not wrong. A reader's sort holds
 * until the restore's rebuild is ready, since the remount comes a commit
 * later. A bare `sortTable`, and the restore the history dispatches, hold
 * until the inner table is ready. A table without `componentOnReady` makes
 * the hold inert.
 *
 * TODO(justin-linn1): file a vets-design-system-documentation bug for the
 * scroll jump, link it here, and remove the hold once va-table holds its own
 * height.
 *
 * @param {Object} props
 * @param {Submission[]} props.submissions The page's rows.
 * @param {string} [props.tableKey] Remounts the table when it changes.
 * @param {(sort: { columnIndex: number, direction: string }) => void} [props.onSortRequest]
 *   Called with the column and direction a reader asked for, in place of
 *   va-table sorting the page itself. Without it va-table sorts as it does.
 * @param {SortRestore|null} [props.restoreSort] The sort to give va-table
 *   back after a remount.
 * @param {(token: number) => void} [props.onRestored] Called with the
 *   restore's `token` once it has been given back, so the parent can clear
 *   it rather than have it replayed when the table mounts again.
 * @param {(submission: Submission) => void} props.onViewFiles Opens an
 *   upload's files, from the View button in its Files / Form cell.
 * @returns {JSX.Element}
 */
const CombinedHistoryTable = ({
  submissions,
  tableKey,
  onSortRequest,
  restoreSort,
  onRestored,
  onViewFiles,
}) => {
  const wrapperRef = useRef(null);
  // The listener reads the latest handler through a ref, so a new handler
  // (the history makes one per URL) does not re-run the effect and clear a
  // fallback timer that is still holding the height.
  const onSortRequestRef = useRef(onSortRequest);
  onSortRequestRef.current = onSortRequest;

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return undefined;

    let latestSort = 0;
    let fallbackTimer = null;

    const release = thisSort => {
      if (thisSort === latestSort) wrapper.style.minHeight = '';
    };
    const releaseWhenReady = async thisSort => {
      await Promise.resolve();
      await wrapper.querySelector('va-table-inner')?.componentOnReady?.();
      release(thisSort);
    };

    const onSortTable = event => {
      latestSort += 1;
      const thisSort = latestSort;
      clearTimeout(fallbackTimer);
      wrapper.style.minHeight = `${wrapper.offsetHeight}px`;

      const requestSort = onSortRequestRef.current;
      if (!requestSort || !isReaderSort(event)) {
        releaseWhenReady(thisSort);
        return;
      }

      event.stopPropagation();
      fallbackTimer = setTimeout(
        () => release(thisSort),
        HEIGHT_HOLD_FALLBACK_MS,
      );
      requestSort({
        columnIndex: Number(event.detail.index),
        direction:
          event.detail.sortdir === 'descending'
            ? COMBINED_HISTORY_DIRECTIONS.DESC
            : COMBINED_HISTORY_DIRECTIONS.ASC,
      });
    };

    wrapper.addEventListener('sortTable', onSortTable, true);
    return () => {
      wrapper.removeEventListener('sortTable', onSortTable, true);
      clearTimeout(fallbackTimer);
    };
  }, []);

  useEffect(() => {
    if (!restoreSort) return undefined;

    let cancelled = false;
    const table = wrapperRef.current?.querySelector('va-table');
    (async () => {
      // A remounted va-table takes `sortTable` before it has loaded, but
      // then sorts nothing and leaves an observer that empties its table.
      // Wait for it, then for the inner table it builds.
      await table?.componentOnReady?.();
      await table?.querySelector('va-table-inner')?.componentOnReady?.();
      await Promise.resolve();
      if (cancelled || !table?.isConnected) return;
      table.dispatchEvent(
        new CustomEvent('sortTable', {
          bubbles: true,
          composed: true,
          detail: {
            index: String(restoreSort.columnIndex),
            sortdir: VA_TABLE_DIRECTIONS[restoreSort.direction],
            restored: true,
          },
        }),
      );
      onRestored?.(restoreSort.token);
    })();
    return () => {
      cancelled = true;
    };
  }, [tableKey, restoreSort, onRestored]);

  if (submissions.length === 0) return null;

  return (
    <div ref={wrapperRef}>
      <va-table
        key={tableKey}
        data-testid="combined-history-table"
        table-title="Submissions"
        sortable
        scrollable
        full-width
        empty-cell-text=""
      >
        <va-table-row slot="headers">
          {COMBINED_HISTORY_COLUMNS.map(column => (
            <span key={column.key}>{column.label}</span>
          ))}
        </va-table-row>
        {submissions.map(submission => {
          const status = statusField(submission);
          const filesOrForm = filesOrFormField(submission);
          const [
            submissionSort,
            claimantSort,
            statusSort,
            confirmationSort,
            filesOrFormSort,
          ] = sortValues(submission, { status, filesOrForm });
          const identifier = maskedIdentifier(submission);
          const submittedOn = shortDateOrBlank(submission.submittedDate);
          return (
            <va-table-row key={submission.id} data-id={submission.id}>
              {/* A cell that stacks two values is blank only when both are,
                  so "None" is announced for the cell, not for each line. */}
              <span data-sort-value={submissionSort}>
                {submittedOn || (!submission.type && orNone(''))}
                <span className="vads-u-display--block">{submission.type}</span>
              </span>
              <span data-sort-value={claimantSort}>
                {claimantLink(submission) || (!identifier && orNone(''))}
                {identifier && (
                  <span
                    className="vads-u-display--block vads-u-margin-top--0p5"
                    data-testid="masked-identifier"
                  >
                    {identifier}
                  </span>
                )}
              </span>
              <span data-sort-value={statusSort}>
                {orNone(status.badge)}
                {status.receivedOn && (
                  <span className={STATUS_DATE}>{status.receivedOn}</span>
                )}
              </span>
              <span
                className="submissions__confirmation"
                data-sort-value={confirmationSort}
              >
                {orNone(submission.confirmationNumber)}
              </span>
              <span data-sort-value={filesOrFormSort}>
                {hasFiles(submission) ? (
                  <span className="submissions__view-files">
                    <ViewFilesButton
                      submission={submission}
                      onView={onViewFiles}
                    />
                    {/* The design shows the bare count here, as "View (4)".
                        Screen readers also hear what it counts. */}
                    {` (${fileCount(submission)}`}
                    <span className="vads-u-visibility--screen-reader">
                      {fileCount(submission) === 1 ? ' file' : ' files'}
                    </span>
                    )
                  </span>
                ) : (
                  orNone(filesOrForm.text)
                )}
              </span>
            </va-table-row>
          );
        })}
      </va-table>
    </div>
  );
};

CombinedHistoryTable.propTypes = {
  submissions: PropTypes.arrayOf(submissionPropType).isRequired,
  onViewFiles: PropTypes.func.isRequired,
  restoreSort: PropTypes.shape({
    columnIndex: PropTypes.number.isRequired,
    direction: PropTypes.oneOf(Object.values(COMBINED_HISTORY_DIRECTIONS))
      .isRequired,
    token: PropTypes.number.isRequired,
  }),
  tableKey: PropTypes.string,
  onRestored: PropTypes.func,
  onSortRequest: PropTypes.func,
};

CombinedHistoryTable.defaultProps = {
  restoreSort: null,
};

// A parent that keeps the page and sort in state re-renders on every change.
export default React.memo(CombinedHistoryTable);
